import { GetStaticProps, InferGetStaticPropsType } from 'next';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { Page } from '@/entities/page';
import { RegistrationType } from '@/entities/registration-type';
import { TeamPerson } from '@/entities/teamPerson';
import { getCommonPageProps } from '@/shared/api/common';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getQuestions } from '@/shared/api/questions';
import { getRegistrationTypes } from '@/shared/api/registration-types';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import ApplicationsSection, { ApplicationsSectionProps } from '@/shared/components/sections/ApplicationsSection';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import Breadcrumbs, { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import Button from '@/shared/components/shared/Button';
import DocumentLink from '@/shared/components/shared/DocumentLink';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import Typografed from '@/shared/components/shared/Typografed';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import { generateRegistrationJsonLd } from '@/shared/lib/utils/registration-json-ld';

const RegistrationPage = ({
    page,
    title,
    personalAccount,
    categories,
    questionsSectionProps,
    faqSectionProps,
    breadcrumbs,
    contactPersonProps,
    applicationsSectionProps,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const registrationPageJsonLd = generateRegistrationJsonLd({
        h1: title,
        description: personalAccount.description || undefined,
        registrationTypes: categories,
        breadcrumbs,
        faqQuestions: faqSectionProps.questions,
    });

    return (
        <DefaultLayout currentPageId={PAGES.REGISTRATION} className="registration">
            <section className=" registration-section-wrapper wrapper js-header-theme-trigger" data-theme="dark">
                <div className="registration-wrapper">
                    {breadcrumbs.length > 0 && (
                        <Breadcrumbs
                            className="breadcrumbs participation-breadcrumbs registration-breadcrumbs"
                            breadcrumbs={breadcrumbs}
                        />
                    )}
                    <div className="registration-welcome">
                        <h1 className="registration__title h2">{title}</h1>
                        <div className="registration-lc__block">
                            {personalAccount.description && (
                                <div className="registration-lc__descr registration-lc__item text-s">
                                    <Typografed>{personalAccount.description}</Typografed>
                                </div>
                            )}
                            {page?.file && (
                                <div className="registration-lc__btn registration-lc__item">
                                    <DocumentLink document={page?.file} />
                                </div>
                            )}
                        </div>
                    </div>
                    {categories.length > 0 && (
                        <div className="registration-categories">
                            <ul className="registration-categories__list list-unstyled">
                                {categories.map((category, index) => (
                                    <li key={index} className="category-item">
                                        <div className="category-item__text-block">
                                            <span className="category-item__title text-xl">
                                                <Typografed>{category.title}</Typografed>
                                            </span>
                                            {category.description && (
                                                <p className="category-item__desc text-s">
                                                    <Typografed>{category.description}</Typografed>
                                                </p>
                                            )}
                                        </div>

                                        <div className="category-item__btn-block">
                                            {category.dashboardLink && (
                                                <Button
                                                    tag={Link}
                                                    href={category.dashboardLink}
                                                    target="_blank"
                                                    rel="noreferrer nofollow"
                                                    variant="outline-dark"
                                                    className="category-item__btn"
                                                    onClick={() => {
                                                        // TODO BACKEND
                                                        // window._txq?.push(['track', category.txqId]);

                                                        if (category.title === 'Участник') {
                                                            window._txq?.push(['track', 'uchastnik']);
                                                        } else if (category.title === 'Посетитель выставки') {
                                                            window._txq?.push(['track', 'posetitel_vystavki']);
                                                        } else if (category.title === 'СМИ') {
                                                            window._txq?.push(['track', 'SMI']);
                                                        }
                                                    }}
                                                >
                                                    Зарегистрироваться
                                                </Button>
                                            )}
                                            {/* временно скрыл */}
                                            {/* {category.detailsLink && (
                                                <Link
                                                    href={category.detailsLink}
                                                    className="category-item__link text-xs"
                                                >
                                                    Подробнее
                                                </Link>
                                            )} */}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </section>

            <ApplicationsSection applicationsSectionProps={applicationsSectionProps} />

            <QuestionsSection questionsSectionProps={questionsSectionProps} contactPersonProps={contactPersonProps} />

            {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                <FaqSection faqSectionProps={faqSectionProps} />
            )}
            <JsonLd<Thing> inBody scriptKey="ld-json-registration" data={registrationPageJsonLd} />
        </DefaultLayout>
    );
};

export default RegistrationPage;

type RegistrationPageProps = NonUndefined<
    CommonPageProps & {
        page?: Page;
        title: string;
        personalAccount: {
            instruction: string;
            description: string | null;
        };
        categories: RegistrationType[];

        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;

        applicationsSectionProps: ApplicationsSectionProps;
        faqSectionProps: FaqSectionProps;
        breadcrumbs: AppBreadcrumbs;
    }
>;

export const getStaticProps: GetStaticProps<RegistrationPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'registration' } });

    const [commonPageProps, questionsResponse, employeesResponse, registrationTypesResponse, siteSettings] =
        await Promise.all([
            getCommonPageProps(pageResponse.data?.id ?? PAGES.REGISTRATION),
            getQuestions({ params: { page: pageResponse.data?.id ?? PAGES.REGISTRATION } }),
            getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.REGISTRATION } }),
            getRegistrationTypes(),
            getSiteSettings({
                params: {
                    keys: [
                        'register_first_screen_title',
                        'register_first_screen_desription',

                        'register_form_title',
                        'register_form_description',
                        'register_form_link_text',
                        'register_form_link',

                        'register_faq_title',
                    ],
                },
            }),
        ]);

    return {
        props: {
            page: pageResponse.data,
            h1: 'Регистрация',
            ...commonPageProps,
            bodyClass: 'registr-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },

            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Регистрация', href: '/registration/' },
            ],

            title: siteSettings.data?.register_first_screen_title || 'Регистрация',
            personalAccount: {
                instruction: 'Инструкция по регистрации',
                description:
                    siteSettings.data?.register_first_screen_desription ||
                    'Личный кабинет TNF — ваше пространство и полезный инструмент в навигации по всем событиям TNF.',
            },
            categories: registrationTypesResponse.data ?? [],
            questionsSectionProps: {
                title: siteSettings.data?.register_form_title || 'Остались вопросы? Напишите нам',
                description: siteSettings.data?.register_form_description || '',
                email: commonPageProps.footerData.email || null,
                phone: commonPageProps.footerData.phone || null,
                href: siteSettings.data?.register_form_link || 'https://t.me/tnf_oilgasforum_bot',
                linkText: siteSettings.data?.register_form_link_text || 'Служба поддержки',
            },
            applicationsSectionProps: {
                title: 'Приложение TNF',
                description:
                    'Личный кабинет TNF доступен и в мобильном приложении. Скачайте его, чтобы пользоваться всеми возможностями форума.',
                stores: [
                    {
                        name: 'Google Play',
                        href: 'https://play.google.com/store/apps/details?id=com.mintrocket.tnf',
                        icon: 'google-play',
                    },
                    {
                        name: 'App Store',
                        href: 'https://apps.apple.com/us/app/tnf/id6779165022',
                        icon: 'app-store',
                    },
                    {
                        name: 'RuStore',
                        href: 'https://www.rustore.ru/catalog/app/com.mintrocket.tnf',
                        icon: 'rustore',
                    },
                ],
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
            faqSectionProps: {
                title: siteSettings.data?.register_faq_title || 'Часто задаваемые вопросы',
                questions: questionsResponse.data ?? [],
            },
        },
        revalidate: 60,
    };
};
